/**
 * cropImage — render a user-selected crop region to a standardized 2K WebP.
 *
 * The Add Screenshot cropper (react-easy-crop) reports the selected region as
 * pixel coordinates in the *source* image (`croppedAreaPixels`). This util maps
 * that region onto a fixed 2560×1440 canvas and exports it as a WebP File, so
 * every Trailblazers screenshot is stored at a uniform 2K, 16:9 resolution.
 */

import type { Area } from 'react-easy-crop';

/** Target output dimensions — standard 2K, 16:9. */
export const OUTPUT_WIDTH = 2560;
export const OUTPUT_HEIGHT = 1440;

/** WebP encode quality (0–1). */
const WEBP_QUALITY = 0.92;

/** Load an image URL into an HTMLImageElement. */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image for cropping'));
    img.src = src;
  });
}

/**
 * Crop `imageSrc` to `croppedAreaPixels` and rasterize onto a 2560×1440 canvas,
 * returning a WebP File. Smaller sources are upscaled to keep the library
 * dimensionally uniform.
 */
export async function getCroppedWebp(imageSrc: string, croppedAreaPixels: Area): Promise<File> {
  const img = await loadImage(imageSrc);

  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_WIDTH;
  canvas.height = OUTPUT_HEIGHT;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2D canvas context');
  ctx.imageSmoothingQuality = 'high';

  // Map the selected source region onto the full 2K canvas.
  ctx.drawImage(
    img,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    OUTPUT_WIDTH,
    OUTPUT_HEIGHT,
  );

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/webp', WEBP_QUALITY),
  );
  if (!blob) throw new Error('Failed to encode cropped image as WebP');

  return new File([blob], 'screenshot.webp', { type: 'image/webp' });
}
